import { useState, useEffect, useCallback } from 'react';
import { hospitalApi } from '../../hospitalApi';

const C = '#1b6ca8';
const DARK = '#0f4c75';
const LIGHT = '#e8f4fd';

const fmtRs = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CATEGORIES = [
  'Doctor Salary', 'Nurse Salary', 'Medicines', 'Equipment',
  'Maintenance', 'Cleaning', 'Utilities', 'Other',
];

const EMPTY = {
  category: 'Other', description: '', amount: '', expenseDate: '',
  paidTo: '', notes: '',
};

const inp = { className: 'form-control' };

function F({ label, children }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {children}
    </div>
  );
}

export default function HospitalExpenses({ role }) {
  const thisMonth = new Date().toISOString().substring(0, 7);
  const [tab, setTab]           = useState('add');
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary]   = useState([]);
  const [month, setMonth]       = useState(thisMonth);
  const [form, setForm]         = useState(EMPTY);
  const [editId, setEditId]     = useState(null);
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState('');

  const loadExpenses = useCallback(() => {
    hospitalApi.getExpenses(month).then(d => { if (d) setExpenses(d); });
  }, [month]);

  const loadSummary = useCallback(() => {
    hospitalApi.getExpensesSummary().then(d => { if (d) setSummary(d); });
  }, []);

  useEffect(() => { loadExpenses(); }, [loadExpenses]);
  useEffect(() => { if (tab === 'summary') loadSummary(); }, [tab, loadSummary]);

  const resetForm = () => { setForm(EMPTY); setEditId(null); setErr(''); };

  const startEdit = (exp) => {
    setForm({
      category: exp.category || 'Other',
      description: exp.description || '',
      amount: exp.amount != null ? String(exp.amount) : '',
      expenseDate: exp.expense_date || '',
      paidTo: exp.paid_to || '',
      notes: exp.notes || '',
    });
    setEditId(exp.id);
    setTab('add');
    setErr('');
  };

  const handleSave = async () => {
    if (!form.amount || !form.expenseDate) { setErr('Amount and Date are required'); return; }
    setSaving(true); setErr('');
    try {
      const payload = { ...form, amount: parseFloat(form.amount) || 0 };
      if (editId) await hospitalApi.updateExpense(editId, payload);
      else await hospitalApi.createExpense(payload);
      loadExpenses();
      resetForm();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    await hospitalApi.deleteExpense(id);
    loadExpenses();
  };

  const totalExpenses = expenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
  const maxSummary = summary.length > 0 ? Math.max(...summary.map(s => Number(s.total || 0))) : 1;

  return (
    <div>
      <div className="tabs" style={{ marginBottom: 16 }}>
        {[
          { key: 'add', label: '➕ Add Expense' },
          { key: 'summary', label: '📊 Monthly Summary' },
        ].map(t => (
          <button key={t.key} className={`tab${tab === t.key ? ' active' : ''}`}
            onClick={() => { setTab(t.key); if (t.key === 'add') resetForm(); }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Add Expense Tab ── */}
      {tab === 'add' && (
        <div>
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header">
              <div className="card-title">{editId ? 'Edit Expense' : 'Record Expense'}</div>
              {editId && (
                <button style={{ background: '#f1f3f4', border: 'none', borderRadius: 6,
                  padding: '5px 12px', cursor: 'pointer', fontSize: 13 }}
                  onClick={resetForm}>+ New Expense</button>
              )}
            </div>
            <div className="card-body">
              {err && <div style={{ color: '#c5221f', background: '#fce8e6', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 13 }}>⚠️ {err}</div>}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
                <F label="Category">
                  <select {...inp} value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                    {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                </F>
                <F label="Amount (₹) *">
                  <input {...inp} type="number" min={0} value={form.amount}
                    onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} />
                </F>
                <F label="Date *">
                  <input {...inp} type="date" value={form.expenseDate}
                    onChange={e => setForm(f => ({ ...f, expenseDate: e.target.value }))} />
                </F>
                <F label="Paid To">
                  <input {...inp} value={form.paidTo} placeholder="Person or vendor name"
                    onChange={e => setForm(f => ({ ...f, paidTo: e.target.value }))} />
                </F>
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label className="form-label">Description</label>
                  <input {...inp} value={form.description} placeholder="Brief description"
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                </div>
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label className="form-label">Notes</label>
                  <textarea {...inp} rows={2} value={form.notes}
                    onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} style={{ resize: 'vertical' }} />
                </div>
              </div>
              <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : editId ? '💾 Update' : '➕ Add Expense'}
                </button>
                {editId && (
                  <button style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e8eaed',
                    background: '#fff', cursor: 'pointer', fontSize: 13 }}
                    onClick={resetForm}>Cancel</button>
                )}
              </div>
            </div>
          </div>

          {/* Expense list */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">Expenses — {month}</div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <input type="month" className="form-control" value={month}
                  onChange={e => setMonth(e.target.value)} style={{ width: 160 }} />
                <div style={{ fontWeight: 700, color: '#27ae60', fontSize: 14 }}>
                  Total: {fmtRs(totalExpenses)}
                </div>
              </div>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              {expenses.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: '#9aa0a6' }}>No expenses for this month</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: LIGHT }}>
                      {['Date', 'Category', 'Description', 'Paid To', 'Amount', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: DARK, fontWeight: 700, fontSize: 12 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.map((e, i) => (
                      <tr key={e.id} style={{ borderTop: '1px solid #f1f3f4', background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                        <td style={{ padding: '10px 12px', color: '#5f6368' }}>{e.expense_date}</td>
                        <td style={{ padding: '10px 12px' }}>
                          <span style={{ background: LIGHT, color: DARK, borderRadius: 5,
                            padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                            {e.category}
                          </span>
                        </td>
                        <td style={{ padding: '10px 12px', color: '#5f6368' }}>{e.description || '—'}</td>
                        <td style={{ padding: '10px 12px', color: '#5f6368' }}>{e.paid_to || '—'}</td>
                        <td style={{ padding: '10px 12px', fontWeight: 700, color: '#c5221f' }}>
                          {fmtRs(e.amount)}
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button style={{ fontSize: 11, padding: '3px 8px', background: LIGHT,
                              color: DARK, border: `1px solid ${C}`, borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}
                              onClick={() => startEdit(e)}>Edit</button>
                            <button style={{ fontSize: 11, padding: '3px 8px', background: '#fce8e6',
                              color: '#c5221f', border: '1px solid #f5c6c6', borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}
                              onClick={() => handleDelete(e.id)}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: LIGHT, borderTop: '2px solid #d0e8f7' }}>
                      <td colSpan={4} style={{ padding: '10px 12px', fontWeight: 700, color: DARK, fontSize: 14 }}>Total</td>
                      <td style={{ padding: '10px 12px', fontWeight: 800, color: '#c5221f', fontSize: 14 }}>
                        {fmtRs(totalExpenses)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Monthly Summary Tab ── */}
      {tab === 'summary' && (
        <div>
          <div className="card">
            <div className="card-header">
              <div className="card-title">📊 Expense Summary — Current Month</div>
              <div style={{ fontWeight: 700, color: '#c5221f', fontSize: 14 }}>
                Total: {fmtRs(summary.reduce((s, e) => s + Number(e.total || 0), 0))}
              </div>
            </div>
            <div className="card-body">
              {summary.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#9aa0a6', padding: 24 }}>No expenses this month</div>
              ) : (
                <>
                  <div style={{ marginBottom: 20 }}>
                    {summary.map((s, i) => {
                      const pct = Math.round((Number(s.total || 0) / maxSummary) * 100);
                      return (
                        <div key={i} style={{ marginBottom: 12 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                            <span style={{ fontWeight: 600, color: '#202124' }}>{s.category}</span>
                            <span style={{ color: '#5f6368', fontSize: 11 }}>{s.cnt} entries</span>
                            <span style={{ fontWeight: 800, color: '#c5221f' }}>{fmtRs(s.total)}</span>
                          </div>
                          <div style={{ height: 10, background: '#e8eaed', borderRadius: 5, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: pct + '%',
                              background: `linear-gradient(90deg, ${C}, #3498db)`, borderRadius: 5 }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Summary table */}
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: LIGHT }}>
                        <th style={{ padding: '10px 12px', textAlign: 'left', color: DARK, fontWeight: 700 }}>Category</th>
                        <th style={{ padding: '10px 12px', textAlign: 'center', color: DARK, fontWeight: 700 }}>Entries</th>
                        <th style={{ padding: '10px 12px', textAlign: 'right', color: DARK, fontWeight: 700 }}>Amount</th>
                        <th style={{ padding: '10px 12px', textAlign: 'right', color: DARK, fontWeight: 700 }}>% Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.map((s, i) => {
                        const grandTotal = summary.reduce((acc, e) => acc + Number(e.total || 0), 0);
                        const share = grandTotal > 0 ? ((Number(s.total || 0) / grandTotal) * 100).toFixed(1) : '0';
                        return (
                          <tr key={i} style={{ borderTop: '1px solid #f1f3f4', background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                            <td style={{ padding: '10px 12px', fontWeight: 600, color: DARK }}>{s.category}</td>
                            <td style={{ padding: '10px 12px', textAlign: 'center', color: '#5f6368' }}>{s.cnt}</td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: '#c5221f' }}>{fmtRs(s.total)}</td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', color: '#5f6368' }}>{share}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: LIGHT, borderTop: '2px solid #d0e8f7' }}>
                        <td style={{ padding: '10px 12px', fontWeight: 800, color: DARK }}>Total</td>
                        <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: '#5f6368' }}>
                          {summary.reduce((s, e) => s + Number(e.cnt || 0), 0)}
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 800, color: '#c5221f', fontSize: 15 }}>
                          {fmtRs(summary.reduce((s, e) => s + Number(e.total || 0), 0))}
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: C }}>100%</td>
                      </tr>
                    </tfoot>
                  </table>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
